import React, { useState, useEffect } from 'react'
import { get } from 'lodash'
import { PlusIcon } from '@heroicons/react/outline'

import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import useFacility from '../../utilities/hooks/useFacility'
import MultiStudentDiscount from './MultiStudentDiscount/MultiStudentDiscount'
import MultiKlassDiscount from './MultiKlassDiscount/MultiKlassDiscount'
import CouponCodeModal from './CouponCodeModal/CouponCodeModal'
import CouponCodesTable from './CouponCodesTable/CouponCodesTable'
import { requestCreator } from '../../utilities/requests'

const { get: getDiscounts, cancel: cancelDiscountsRequests } = requestCreator()

const MODAL_STATES = { closed: '', new: 'new', edit: 'edit' }

function FacilitiesDiscountsIndex() {
  const { facility, isLoading: isFacilityLoading } = useFacility()
  const [couponCodes, setCouponCodes] = useState([])
  const [multiStudentDiscount, setMultiStudentDiscount] = useState(null)
  const [multiKlassDiscount, setMultiKlassDiscount] = useState(null)
  const [isLoadingDiscounts, setIsLoadingDiscounts] = useState(true)
  const [couponCodeModal, setCouponCodeModal] = useState(MODAL_STATES.closed)
  const [couponCodeForEdit, setCouponCodeForEdit] = useState(null)

  useEffect(() => {
    if (!isFacilityLoading) {
      getDiscounts(`/api/facilities/${facility.id}/discounts`).then((retrievedDiscounts) => {
        setCouponCodes(retrievedDiscounts.filter((discount) => discount.discountableType === 'CouponCode'))
        setMultiStudentDiscount(
          retrievedDiscounts.find((discount) => discount.description === 'Multi-student discount')
        )
        setMultiKlassDiscount(retrievedDiscounts.find((discount) => discount.description === 'Multi-class discount'))
        setIsLoadingDiscounts(false)
      })
    }

    return cancelDiscountsRequests
  }, [isFacilityLoading, facility.id])

  function handleEditCouponCode(couponCode) {
    setCouponCodeModal(MODAL_STATES.edit)
    setCouponCodeForEdit(couponCode)
  }

  return (
    <ApplicationLayout>
      <div className="min-h-screen bg-gray-100 pb-10">
        <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Discounts</h1>
              <p className="mt-1 text-sm text-gray-600 sm:mt-2">Manage automatic discounts and coupon codes</p>
            </div>
          </div>

          <div className="mb-8 overflow-hidden bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Automatic Discounts</h2>
              <p className="mt-1 text-sm text-gray-600">
                Discounts applied automatically at checkout. Only the greater of the two is applied.
              </p>
            </div>
            <div className="px-6 py-6">
              {isLoadingDiscounts ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-12 h-12 border-4 border-purple-600 rounded-full border-t-transparent animate-spin"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  <MultiStudentDiscount
                    amount={get(multiStudentDiscount, 'amount')}
                    active={get(multiStudentDiscount, 'active')}
                    reductionMethod={get(multiStudentDiscount, 'reductionMethod')}
                  />

                  <div className="border-t border-gray-200 pt-6">
                    <MultiKlassDiscount
                      amount={get(multiKlassDiscount, 'amount')}
                      active={get(multiKlassDiscount, 'active')}
                      reductionMethod={get(multiKlassDiscount, 'reductionMethod')}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="overflow-hidden bg-white rounded-lg shadow">
            <div className="flex flex-col gap-4 px-6 py-4 border-b border-gray-200 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Coupon Codes</h2>
                <p className="mt-1 text-sm text-gray-600">Create codes that can be applied at checkout</p>
              </div>
              <button
                onClick={() => setCouponCodeModal(MODAL_STATES.new)}
                className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md sm:w-auto hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                data-cy="add-coupon-code-button"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add New
              </button>
            </div>
            {isLoadingDiscounts ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-purple-600 rounded-full border-t-transparent animate-spin"></div>
              </div>
            ) : (
              <CouponCodesTable
                couponCodes={couponCodes}
                onCouponCodesChange={setCouponCodes}
                onEditCouponCode={handleEditCouponCode}
                facilityId={facility.id}
              />
            )}
          </div>
        </div>
      </div>

      <CouponCodeModal
        discount={couponCodeForEdit}
        open={couponCodeModal !== MODAL_STATES.closed}
        header={`${couponCodeModal === MODAL_STATES.new ? 'New' : 'Edit'} Coupon Code`}
        onClose={(discount) => {
          setCouponCodeModal(MODAL_STATES.closed)
          setCouponCodeForEdit(null)
          if (discount) {
            setCouponCodes((previousCodes) => {
              const existingIndex = previousCodes.map(({ id }) => id).indexOf(discount.id)
              if (existingIndex !== -1) {
                const newCodes = [...previousCodes]
                newCodes[existingIndex] = discount
                return newCodes
              } else {
                return [discount, ...previousCodes]
              }
            })
          }
        }}
      />
    </ApplicationLayout>
  )
}

export default FacilitiesDiscountsIndex
